import { useState, useCallback, useRef } from 'react';
import { ProductEngine } from '@services/product-engine';
import type { Answer, ClientInfo } from '@/types';

interface FormState {
  currentStep: number;
  answers: Map<string, Answer>;
  clientInfo: ClientInfo | null;
  totalPoints: number;
}

interface UseFormDataReturn {
  // State
  currentStep: number;
  answers: Map<string, Answer>;
  clientInfo: ClientInfo | null;
  totalPoints: number;

  // Actions
  saveAnswer: (questionId: string, answer: Answer) => void;
  removeAnswer: (questionId: string) => void;
  saveClientInfo: (info: ClientInfo) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  resetForm: () => void;

  // Computed
  canGoNext: boolean;
  canGoPrevious: boolean;
  progress: number;
  getProductRecommendations: () => string[];
  getBonusProducts: () => string[];
}

export const useFormData = (totalSteps: number): UseFormDataReturn => {
  const productEngine = useRef(new ProductEngine());

  // Always start fresh - no persistence
  const loadInitialState = (): FormState => {
    return {
      currentStep: 0,
      answers: new Map(),
      clientInfo: null,
      totalPoints: 0,
    };
  };

  const [state, setState] = useState<FormState>(loadInitialState);

  // No need to rebuild from saved state anymore
  // useEffect(() => {
  //   if (state.answers.size > 0) {
  //     console.log('🔄 Rebuilding ProductEngine from saved state...');
  //     productEngine.current.reset();

  //     state.answers.forEach((answer, questionId) => {
  //       if (answer.products && answer.products.length > 0) {
  //         productEngine.current.addRecommendation(questionId, answer.products, answer.points);
  //       }
  //       if (answer.exclusions && answer.exclusions.length > 0) {
  //         productEngine.current.addExclusion(answer.exclusions);
  //       }
  //       if (answer.alternatives) {
  //         Object.entries(answer.alternatives).forEach(([orig, alt]) => {
  //           productEngine.current.addAlternative(orig, alt);
  //         });
  //       }
  //     });

  //     console.log(`✅ ProductEngine rebuilt with ${state.totalPoints} points`);
  //   }
  // }, []);

  // No longer saving to localStorage - always start fresh
  // useEffect(() => {
  //   const dataToSave = {
  //     currentStep: state.currentStep,
  //     answers: Array.from(state.answers.entries()),
  //     clientInfo: state.clientInfo,
  //     totalPoints: state.totalPoints,
  //     savedAt: new Date().toISOString(),
  //   };
  //   localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  // }, [state]);

  const saveAnswer = useCallback((questionId: string, answer: Answer) => {
    setState(prev => {
      const newAnswers = new Map(prev.answers);
      const previousAnswer = newAnswers.get(questionId);

      // Remove previous answer's effects if exists
      if (previousAnswer) {
        if (previousAnswer.products && previousAnswer.products.length > 0) {
          productEngine.current.removeRecommendation(
            questionId,
            previousAnswer.products,
            previousAnswer.points
          );
        }
      }

      // Add new answer
      newAnswers.set(questionId, answer);

      // Update ProductEngine
      if (answer.products && answer.products.length > 0) {
        productEngine.current.addRecommendation(questionId, answer.products, answer.points);
      }
      if (answer.exclusions && answer.exclusions.length > 0) {
        productEngine.current.addExclusion(answer.exclusions);
      }
      if (answer.alternatives) {
        Object.entries(answer.alternatives).forEach(([orig, alt]) => {
          productEngine.current.addAlternative(orig, alt);
        });
      }

      const newTotalPoints = productEngine.current.getTotalPoints();

      return {
        ...prev,
        answers: newAnswers,
        totalPoints: newTotalPoints,
      };
    });
  }, []);

  const removeAnswer = useCallback((questionId: string) => {
    setState(prev => {
      const newAnswers = new Map(prev.answers);
      const answer = newAnswers.get(questionId);

      if (answer) {
        // Remove from ProductEngine
        if (answer.products && answer.products.length > 0) {
          productEngine.current.removeRecommendation(questionId, answer.products, answer.points);
        }
        newAnswers.delete(questionId);
      }

      const newTotalPoints = productEngine.current.getTotalPoints();

      return {
        ...prev,
        answers: newAnswers,
        totalPoints: newTotalPoints,
      };
    });
  }, []);

  const saveClientInfo = useCallback((info: ClientInfo) => {
    setState(prev => ({
      ...prev,
      clientInfo: info,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, totalSteps),
    }));
  }, [totalSteps]);

  const previousStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      setState(prev => ({
        ...prev,
        currentStep: Math.max(0, Math.min(step, totalSteps)),
      }));
    },
    [totalSteps]
  );

  const resetForm = useCallback(() => {
    productEngine.current.reset();
    setState({
      currentStep: 0,
      answers: new Map(),
      clientInfo: null,
      totalPoints: 0,
    });
    console.log('🗑️ Form reset');
  }, []);

  const getProductRecommendations = useCallback(() => {
    return productEngine.current.getProductCodes();
  }, []);

  const getBonusProducts = useCallback(() => {
    const recommendations = productEngine.current.getRecommendations();
    const bonusProducts = recommendations.filter(rec => 
      rec.reason.includes('bonus-points') || rec.reason.includes('bonus-points-replacement')
    );
    return bonusProducts.map(rec => rec.code);
  }, []);

  // Computed values
  const canGoNext = state.currentStep < totalSteps;
  const canGoPrevious = state.currentStep > 0;
  const progress = totalSteps > 0 ? ((state.currentStep + 1) / totalSteps) * 100 : 0;

  return {
    // State
    currentStep: state.currentStep,
    answers: state.answers,
    clientInfo: state.clientInfo,
    totalPoints: state.totalPoints,

    // Actions
    saveAnswer,
    removeAnswer,
    saveClientInfo,
    nextStep,
    previousStep,
    goToStep,
    resetForm,

    // Computed
    canGoNext,
    canGoPrevious,
    progress,
    getProductRecommendations,
    getBonusProducts,
  };
};
